import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useRef } from 'react';
import { useAppDispatch } from 'src/hooks/store';
import { hideModal } from 'src/state/slices/modal';
import MetrobiEligibilityTab from './MetrobiEligibilityTab';
import { useMetrobiAdmin } from '../../LocationsTable/metrobi';
import { Spacer, Tabs } from '@odekoteam/doppio';
import { useAppSelector } from 'src/hooks/store';
import { modalSelector } from 'src/state/selectors/modal';
import { Flex } from 'theme-ui';
import { messageStyle, styledConfirmationModal } from '../styles';
import AutomatedRoutingTab from './AutomatedRoutingTab';
import CohortsTab from './CohortsTab';
import CustomerPickupTab from './CustomerPickupTab';
import DeliverWithinStoreHoursTab from './DeliverWithinStoreHoursTab';
import EnforceItemScanningTab from './EnforceItemScanningTab';
import LocationServiceTimeTab from './LocationServiceTimeTab';
import PrintDeliverySheetTab from './PrintDeliverySheetTab';
import IncludeFinancialsTab from './IncludeFinancialsTab';

export enum BulkEditTab {
  MetrobiEligibility = 'MetrobiEligibility',
  Cohorts = 'Cohorts',
  AutomatedRouting = 'AutomatedRouting',
  LocationServiceTime = 'LocationServiceTime',
  EnforceItemScanning = 'EnforceItemScanning',
  DeliverWithinStoreHours = 'DeliverWithinStoreHours',
  CustomerPickup = 'CustomerPickup',
  PrintDeliverySheet = 'PrintDeliverySheet',
  IncludeFinancials = 'IncludeFinancials',
}

const isBulkEditTab = (value: unknown): value is BulkEditTab =>
  Object.values(BulkEditTab).includes(value as BulkEditTab);

const BulkEditLocationsModal = (): JSX.Element | null => {
  const { deliveryServiceMetrobiEligibility = false } = useFlags();
  const isAdmin = useMetrobiAdmin();
  const dispatch = useAppDispatch();
  const dialogRef = useRef<HTMLDivElement>(null);
  const metrobi = useAppSelector((state) => state.locations.metrobi);
  // Every tab that writes has to raise this, or the dialog stays interactive
  // over an in-flight request: Escape closes it, a tab switch unmounts the tab
  // mid-write, and another tab's success can hide a dialog this one opened.
  const pending =
    Boolean(metrobi?.pendingIds.length) ||
    Boolean(metrobi?.customerPickupPending);
  const modal = useAppSelector(modalSelector);
  const requestedInitialTab = modal.variables?.initialTab;
  const initialTab = isBulkEditTab(requestedInitialTab)
    ? requestedInitialTab
    : BulkEditTab.Cohorts;

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return (): void => {
      if (previous?.isConnected) previous.focus();
      else
        (
          document.getElementById('metrobi-update-status') ??
          document.getElementById('locations-table')
        )?.focus();
    };
  }, []);
  useEffect(() => {
    if (pending) dialogRef.current?.focus();
  }, [pending]);
  useEffect(() => {
    if (
      modal.variables?.metrobiContextId &&
      modal.variables.metrobiContextId !== metrobi?.contextId
    )
      dispatch(hideModal());
  }, [modal.variables?.metrobiContextId, metrobi?.contextId, dispatch]);

  return (
    <div
      id="automated-routing-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bulk-locations-title"
      tabIndex={-1}
      ref={dialogRef}
      aria-busy={pending}
      onClickCapture={(event): void => {
        if (pending) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      onKeyDownCapture={(event): void => {
        if (pending) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      onKeyDown={(event): void => {
        if (event.key === 'Escape' && !pending) dispatch(hideModal());
        if (event.key !== 'Tab') return;
        const focusable = Array.from(
          dialogRef.current?.querySelectorAll<HTMLElement>(
            'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), [tabindex="0"]',
          ) ?? [],
        ).filter(
          (element) =>
            element.getAttribute('aria-disabled') !== 'true' &&
            !element.closest('[hidden]'),
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first) {
          event.preventDefault();
          return;
        }
        if (
          event.shiftKey &&
          (document.activeElement === first ||
            document.activeElement === dialogRef.current)
        ) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }}
      sx={{ ...styledConfirmationModal, maxWidth: '1300px' }}
    >
      <Flex id="bulk-locations-title" sx={messageStyle}>
        Update locations
      </Flex>
      <Spacer height="$4" />
      {modal.variables?.metrobiOnly ? (
        <MetrobiEligibilityTab />
      ) : (
        <Tabs.Root defaultValue={initialTab}>
          {[
            {
              value: BulkEditTab.Cohorts,
              label: 'Cohorts',
              Component: CohortsTab,
            },
            {
              value: BulkEditTab.AutomatedRouting,
              label: 'Automated Routing',
              Component: AutomatedRoutingTab,
            },
            {
              value: BulkEditTab.EnforceItemScanning,
              label: 'Enforce Item Scanning',
              Component: EnforceItemScanningTab,
            },
            {
              value: BulkEditTab.DeliverWithinStoreHours,
              label: 'Deliver Within Store Hours',
              Component: DeliverWithinStoreHoursTab,
            },
            {
              value: BulkEditTab.CustomerPickup,
              label: 'Customer Pickup',
              Component: CustomerPickupTab,
            },
            {
              value: BulkEditTab.LocationServiceTime,
              label: 'Location Service Time',
              Component: LocationServiceTimeTab,
            },
            {
              value: BulkEditTab.PrintDeliverySheet,
              label: 'Print Delivery Sheet',
              Component: PrintDeliverySheetTab,
            },
            {
              value: BulkEditTab.IncludeFinancials,
              label: 'Include Financials on Delivery Sheet',
              Component: IncludeFinancialsTab,
            },
          ].map(({ value, label, Component }) => (
            <Tabs.Item
              value={value}
              label={label}
              key={value}
              // TODO: add id prop to Doppio Tab
              // @ts-ignore
              nativeID={`tab-${value}`}
            >
              <div sx={{ marginTop: '24px' }}>
                <Component />
              </div>
            </Tabs.Item>
          ))}
          {deliveryServiceMetrobiEligibility &&
            isAdmin &&
            modal.variables?.metrobiContextId && (
              <Tabs.Item
                id={`tab-${BulkEditTab.MetrobiEligibility}`}
                value={BulkEditTab.MetrobiEligibility}
                label="Metrobi Eligible"
                key={BulkEditTab.MetrobiEligibility}
              >
                <div sx={{ marginTop: '24px' }}>
                  <MetrobiEligibilityTab />
                </div>
              </Tabs.Item>
            )}
        </Tabs.Root>
      )}
    </div>
  );
};
export default BulkEditLocationsModal;
